import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { ScrollPanel } from 'primereact/scrollpanel';

import './ChatBox.css';

const ChatBox = () => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const toast = useRef(null);
  const scrollPanelRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_PATH || 'https://localhost:7000/api';

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
    // Poll for new messages every 10 seconds
    const interval = setInterval(() => {
      fetchContacts();
      if (selectedContact) {
        fetchMessages(selectedContact.id);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}whatsapp/contacts`);
      console.log('Contacts fetched:', response.data);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch contacts',
        life: 3000
      });
    }
  };

  const fetchMessages = async (contactId) => {
    if (!contactId) return;
    
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API_BASE_URL}whatsapp/messages`, {
        params: { contactId }
      });
      console.log('Messages fetched:', response.data);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch messages',
        life: 3000
      });
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleContactSelect = (contact) => {
    console.log('Selected contact:', contact);
    setSelectedContact(contact);
    if (contact && contact.id) {
      fetchMessages(contact.id);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;

    setSending(true);
    try {
      const response = await axios.post(`${API_BASE_URL}whatsapp/send`, {
        to: selectedContact.phonenumber || selectedContact.phoneNumber,
        text: newMessage.trim(),
        businessPhoneNumberId: process.env.REACT_APP_BUSINESS_PHONE_NUMBER_ID
      });

      if (response.status === 200) {
        setNewMessage('');
        // Refresh messages after sending
        await fetchMessages(selectedContact.id);
        // Update contacts to show latest message
        await fetchContacts();
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Message sent successfully',
          life: 2000
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to send message',
        life: 3000
      });
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    if (scrollPanelRef.current) {
      const panel = scrollPanelRef.current.getContainer();
      if (panel) {
        setTimeout(() => {
          panel.scrollTop = panel.scrollHeight;
        }, 100);
      }
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Check if message is outgoing (sent by user)
  const isOutgoingMessage = (message) => {
    return message.type === 'OUT' || message.type === 'sent' || message.type === 'SENT';
  };

  // Check if contact has data
  const hasContactData = (contact) => {
    return contact && contact.id && contact.phonenumber;
  };

  return (
    <div className="chat-container">
      <Toast ref={toast} />
      
      <div className="chat-wrapper">
        {/* Contacts Sidebar */}
        <div className="contacts-sidebar">
          <div className="contacts-header">
            <h2>WhatsApp Chats</h2>
            <div className="contacts-count">
              <Badge value={contacts.length} severity="info" />
            </div>
          </div>
          
          <Divider />
          
          <ScrollPanel style={{ height: 'calc(100% - 80px)' }}>
            {contacts.length === 0 ? (
              <div className="no-contacts">
                <i className="pi pi-whatsapp" style={{ fontSize: '3rem', color: '#25D366' }}></i>
                <p>No contacts yet</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.id || contact.phonenumber}
                  className={`contact-item ${selectedContact?.id === contact.id ? 'active' : ''}`}
                  onClick={() => handleContactSelect(contact)}
                >
                  <Avatar 
                    label={getInitials(contact.name)} 
                    shape="circle" 
                    size="large"
                    style={{ backgroundColor: '#25D366', color: '#ffffff' }}
                  />
                  <div className="contact-info">
                    <div className="contact-name">{contact.name || contact.phonenumber}</div>
                    <div className="contact-last-message">
                      {contact.lastmessage || 'No messages yet'}
                    </div>
                  </div>
                  <div className="contact-time">
                    {contact.lastmessagetime && formatTime(contact.lastmessagetime)}
                  </div>
                </div>
              ))
            )}
          </ScrollPanel>
        </div>

        {/* Chat Area */}
        <div className="chat-area">
          {!selectedContact || !hasContactData(selectedContact) ? (
            <div className="no-chat-selected">
              <i className="pi pi-comments" style={{ fontSize: '4rem', color: '#ccc' }}></i>
              <h3>Select a contact to start chatting</h3>
              <p>Choose a contact from the sidebar</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-header-info">
                  <Avatar 
                    label={getInitials(selectedContact.name)} 
                    shape="circle" 
                    size="large"
                    style={{ backgroundColor: '#25D366', color: '#ffffff' }}
                  />
                  <div className="chat-header-details">
                    <div className="chat-header-name">{selectedContact.name || selectedContact.phonenumber}</div>
                    <div className="chat-header-phone">{selectedContact.phonenumber}</div>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="messages-area">
                <ScrollPanel ref={scrollPanelRef} style={{ height: '100%' }}>
                  {loadingMessages ? (
                    <div className="loading-messages">
                      <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                      <p>Loading messages...</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="no-messages">
                      <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                      <p>No messages yet</p>
                      <p className="no-messages-sub">Send a message to start the conversation</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => {
                        const showDate = index === 0 || 
                          (message.timestamp && messages[index - 1]?.timestamp && 
                          new Date(message.timestamp).toDateString() !== 
                          new Date(messages[index - 1].timestamp).toDateString());
                        
                        const isOutgoing = isOutgoingMessage(message);
                        
                        return (
                          <React.Fragment key={message.id || index}>
                            {showDate && (
                              <div className="message-date-divider">
                                <span>{formatDate(message.timestamp)}</span>
                              </div>
                            )}
                            <div className={`message ${isOutgoing ? 'message-out' : 'message-in'}`}>
                              <div className="message-bubble">
                                <div className="message-text">{message.body || 'No content'}</div>
                                <div className="message-time">
                                  {formatTime(message.timestamp)}
                                  {isOutgoing && (
                                    <i className={`pi ${message.isRead ? 'pi-check-circle' : 'pi-check'} message-status`}></i>
                                  )}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </ScrollPanel>
              </div>

              {/* Message Input */}
              <div className="message-input-area">
                <div className="message-input-wrapper">
                  <InputTextarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    rows={1}
                    className="message-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={sending}
                  />
                  <Button
                    icon="pi pi-send"
                    className="send-button"
                    onClick={sendMessage}
                    loading={sending}
                    disabled={!newMessage.trim() || sending}
                    rounded
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatBox;
